import { useSearchParams } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { Input } from '#app/shared/ui/inputs/Input.js'

type RangeInputProps = {
  title: string
  minParamName: string
  maxParamName: string
  suffix?: React.ReactNode
  validate?: (min: number, max: number) => boolean
  containerClasses?: string
  inputClasses?: string
}

export const RangeInput = ({
  title,
  minParamName,
  maxParamName,
  suffix,
  validate,
  containerClasses = 'w-full min-w-[150px] max-w-[182px] h-full',
  inputClasses = `
    [-moz-appearance:textfield]
    [&::-webkit-outer-spin-button]:m-0
    [&::-webkit-outer-spin-button]:appearance-none
    [&::-webkit-inner-spin-button]:m-0
    [&::-webkit-inner-spin-button]:appearance-none
    pr-8
  `,
}: RangeInputProps) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [minValue, setMinValue] = useState(searchParams.get(minParamName) || '')
  const [maxValue, setMaxValue] = useState(searchParams.get(maxParamName) || '')

  useEffect(() => {
    setMinValue(searchParams.get(minParamName) || '')
    setMaxValue(searchParams.get(maxParamName) || '')
  }, [searchParams, minParamName, maxParamName])

  const validateRange = (newMin: string, newMax: string): boolean => {
    if (!newMin || !newMax) return true

    const min = parseInt(newMin, 10)
    const max = parseInt(newMax, 10)

    if (min > max) return false

    if (validate && !validate(min, max)) return false

    return true
  }

  const updateSearchParams = (min: string, max: string) => {
    const currentParams = Object.fromEntries(searchParams.entries())
    const updatedParams = { ...currentParams }

    if (min === '') {
      delete updatedParams[minParamName]
    } else {
      updatedParams[minParamName] = min
    }

    if (max === '') {
      delete updatedParams[maxParamName]
    } else {
      updatedParams[maxParamName] = max
    }

    setSearchParams(updatedParams)
  }

  const handleMinChange = (value: string) => {
    setMinValue(value)

    if (validateRange(value, maxValue)) {
      updateSearchParams(value, maxValue)
    }
  }

  const handleMaxChange = (value: string) => {
    setMaxValue(value)

    if (validateRange(minValue, value)) {
      updateSearchParams(minValue, value)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base-bold">{title}</h3>
      <div
        className={`
          flex flex-row gap-2

          lg:gap-7
        `}
      >
        <Input
          type="number"
          className={containerClasses}
          inputClassName={inputClasses}
          placeholder="Desde"
          onChange={(e) => handleMinChange(e.target.value)}
          rightIcon={suffix}
          defaultValue={minValue}
        />
        <p className="flex items-center text-base-medium text-neutral-500">
          hasta
        </p>
        <Input
          type="number"
          className={containerClasses}
          inputClassName={inputClasses}
          placeholder="Hasta"
          onChange={(e) => handleMaxChange(e.target.value)}
          rightIcon={suffix}
          defaultValue={maxValue}
        />
      </div>
    </div>
  )
}
